import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  ControlLabel,
  Row,
  Col,
  FormGroup,
  HelpBlock,
} from 'react-bootstrap';
import dropdownLocations from 'constants/dropdownLocations';
import styles from './DualLocationDropdown.module.scss';

const DualLocationDropdown = ({
  defaultCountry = '',
  defaultCity = '',
  onChangePlace,
  validationState = null,
  layout = 'horizontal', // 'horizontal' or 'vertical'
}) => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const { countries, citiesByCountries } = dropdownLocations;

  // Initialize with default values
  useEffect(() => {
    if (defaultCountry) {
      // Find country code from country name
      const countryCode = Object.keys(countries).find(
        (code) => countries[code].toLowerCase() === defaultCountry.toLowerCase()
      );
      if (countryCode) {
        setSelectedCountry(countryCode);
        setCountrySearch(countries[countryCode]);
      } else {
        setCountrySearch(defaultCountry);
      }
    }
    if (defaultCity) {
      setSelectedCity(defaultCity);
      setCitySearch(defaultCity);
    }
  }, [defaultCountry, defaultCity, countries]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return Object.entries(countries);

    return Object.entries(countries).filter(([, name]) =>
      name.toLowerCase().includes(countrySearch.toLowerCase())
    );
  }, [countrySearch, countries]);

  // Get cities for selected country or all cities if no country selected
  const availableCities = useMemo(() => {
    if (selectedCountry && citiesByCountries[selectedCountry]) {
      return citiesByCountries[selectedCountry].map((city) => ({
        name: city,
        country: countries[selectedCountry],
        countryCode: selectedCountry,
      }));
    }

    // If no country selected, show all cities with country names
    const allCities = [];
    Object.entries(citiesByCountries).forEach(([countryCode, cities]) => {
      cities.forEach((city) => {
        allCities.push({
          name: city,
          country: countries[countryCode],
          countryCode: countryCode,
        });
      });
    });

    return allCities;
  }, [selectedCountry, citiesByCountries, countries]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return availableCities;

    return availableCities.filter((city) =>
      city.name.toLowerCase().includes(citySearch.toLowerCase())
    );
  }, [citySearch, availableCities]);

  // Handle country selection
  const handleCountrySelect = (countryCode, countryName) => {
    setSelectedCountry(countryCode);
    setCountrySearch(countryName);
    setShowCountryDropdown(false);

    // Clear city if it's not available in the new country
    if (
      selectedCity &&
      citiesByCountries[countryCode] &&
      !citiesByCountries[countryCode].includes(selectedCity)
    ) {
      setSelectedCity('');
      setCitySearch('');
      // Trigger callback with just country (no city)
      triggerLocationChange(countryCode, countryName, '');
    } else {
      // Trigger callback with country and existing city (if any)
      triggerLocationChange(countryCode, countryName, selectedCity);
    }
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    setSelectedCity(city.name);
    setCitySearch(city.name);
    setShowCityDropdown(false);

    // Auto-fill country if not already selected or if city is not in current country
    if (
      !selectedCountry ||
      (citiesByCountries[selectedCountry] &&
        !citiesByCountries[selectedCountry].includes(city.name))
    ) {
      setSelectedCountry(city.countryCode);
      setCountrySearch(city.country);
    }

    // Trigger callback with updated location
    triggerLocationChange(city.countryCode, city.country, city.name);
  };

  // Trigger the location change callback
  const triggerLocationChange = (countryCode, countryName, cityName = '') => {
    if (onChangePlace && countryCode) {
      const locationData = {
        city: cityName,
        country: countryName,
        country_code: countryCode,
        text: cityName ? `${cityName}, ${countryName}` : countryName,
        method: 'html5',
        countryOnly: cityName ? 'specificAddress' : 'countryOnly',
      };
      onChangePlace(locationData);
    }
  };

  // Handle country input change
  const handleCountryInputChange = (e) => {
    const value = e.target.value;
    setCountrySearch(value);
    setShowCountryDropdown(true);

    // If input is cleared, clear selection
    if (!value) {
      setSelectedCountry('');
    }
  };

  // Handle city input change
  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCitySearch(value);
    setShowCityDropdown(true);

    // If input is cleared, clear selection
    if (!value) {
      setSelectedCity('');
    }
  };

  // Determine layout classes and structure
  const isVertical = layout === 'vertical';
  const inputColProps = isVertical ? { xs: 12 } : { sm: 8 };
  const labelClass = isVertical ? styles.verticalLabel : styles.controlLabel;

  return (
    <>
      <Row className="mt++">
        <FormGroup
          controlId="DualLocationCountry"
          validationState={validationState}
        >
          {isVertical && (
            <Col xs={12}>
              <ControlLabel className={labelClass}>Country</ControlLabel>
            </Col>
          )}
          {!isVertical && (
            <Col sm={4} componentClass={ControlLabel} className={labelClass}>
              Country
            </Col>
          )}
          <Col {...inputColProps}>
            <div className={styles.dropdownWrapper}>
              <FormControl
                type="text"
                value={countrySearch}
                onChange={handleCountryInputChange}
                onFocus={() => setShowCountryDropdown(true)}
                onBlur={() => {
                  setTimeout(() => {
                    setShowCountryDropdown(false);
                    // If user typed something but didn't select from dropdown, clear the input
                    if (countrySearch && !selectedCountry) {
                      setCountrySearch('');
                    }
                  }, 200);
                }}
                placeholder="Select your country..."
                className={styles.dropdownInput}
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              {showCountryDropdown && filteredCountries.length > 0 && (
                <div className={styles.dropdownMenu}>
                  {filteredCountries.map(([code, name]) => (
                    <div
                      key={code}
                      className={`${styles.dropdownItem} ${
                        selectedCountry === code ? styles.selected : ''
                      }`}
                      onMouseDown={() => handleCountrySelect(code, name)}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.description}>
              Select your country for location-based job notifications
            </div>
            {validationState === 'error' && (
              <HelpBlock>
                <span className={styles.error}>Please select a country</span>
              </HelpBlock>
            )}
          </Col>
        </FormGroup>
      </Row>
      <Row className="mt++">
        <FormGroup controlId="DualLocationCity">
          {isVertical && (
            <Col xs={12}>
              <ControlLabel className={labelClass}>City</ControlLabel>
            </Col>
          )}
          {!isVertical && (
            <Col sm={4} componentClass={ControlLabel} className={labelClass}>
              City
            </Col>
          )}
          <Col {...inputColProps}>
            <div className={styles.dropdownWrapper}>
              <FormControl
                type="text"
                value={citySearch}
                onChange={handleCityInputChange}
                onFocus={() => setShowCityDropdown(true)}
                onBlur={() => {
                  setTimeout(() => {
                    setShowCityDropdown(false);
                    // If user typed something but didn't select from dropdown, clear the input
                    if (citySearch && !selectedCity) {
                      setCitySearch('');
                    }
                  }, 200);
                }}
                placeholder="Select your nearest city..."
                className={styles.dropdownInput}
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              {showCityDropdown && filteredCities.length > 0 && (
                <div className={styles.dropdownMenu}>
                  {filteredCities.map((city, index) => {
                    return (
                      <div
                        key={`${city.countryCode}-${city.name}-${index}`}
                        className={`${styles.dropdownItem} ${
                          selectedCity === city.name ? styles.selected : ''
                        }`}
                        onMouseDown={() => handleCitySelect(city)}
                      >
                        {selectedCountry ? (
                          city.name
                        ) : (
                          <>
                            {city.name}
                            {', '}
                            <span className={styles.description}>
                              {city.country}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className={styles.description}>
              Receive notifications of jobs in your nearest city
            </div>
          </Col>
        </FormGroup>
      </Row>
    </>
  );
};

DualLocationDropdown.propTypes = {
  defaultCountry: PropTypes.string,
  defaultCity: PropTypes.string,
  onChangePlace: PropTypes.func.isRequired,
  validationState: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
};

export default DualLocationDropdown;
